import { useState, useEffect } from "react";
import { useDebounce } from "@uidotdev/usehooks";

// Public用の型定義
export interface PublicSearchConditions {
  name: string | null;
  phoneticName: string | null;
  location: { lat: number | null; lon: number | null };
  sort: { field: string | null; order: string | null };
}

export interface PublicSearchLocalState {
  nameInput: string;
  phoneticNameInput: string;
  latInput: string;
  lonInput: string;
  sortFieldInput: string;
  sortOrderInput: string;
}

// Draft用の型定義 (Publicを継承)
export interface DraftSearchConditions extends PublicSearchConditions {
  publishedRevisionId: string | null;
  hasUnpublishedChanges: string | null;
}

export interface DraftSearchLocalState extends PublicSearchLocalState {
  publishedRevisionInput: string;
  hasUnpublishedChangesInput: string;
}

// 共通の基底型を定義
type BaseSearchConditions = PublicSearchConditions | DraftSearchConditions;
type BaseSearchLocalState = PublicSearchLocalState | DraftSearchLocalState;

/**
 * 検索タブ用のデバウンス付きローカルstate管理フック（共通版）
 */
export const useDebouncedSearch = <T extends BaseSearchConditions, L extends BaseSearchLocalState>(
  searchConditions: T,
  updateSearchConditions: (updates: Record<string, any>) => void,
  debounceMs: number = 500
) => {
  const [localState, setLocalState] = useState<L>(() => {
    const initialState: any = {
      nameInput: searchConditions.name || "",
      phoneticNameInput: searchConditions.phoneticName || "",
      latInput: searchConditions.location?.lat?.toString() || "",
      lonInput: searchConditions.location?.lon?.toString() || "",
      sortFieldInput: searchConditions.sort?.field || "",
      sortOrderInput: searchConditions.sort?.order || ""
    };
    if ("publishedRevisionId" in searchConditions) {
      initialState.publishedRevisionInput = searchConditions.publishedRevisionId || "";
    }
    if ("hasUnpublishedChanges" in searchConditions) {
      initialState.hasUnpublishedChangesInput = searchConditions.hasUnpublishedChanges || "";
    }
    return initialState as L;
  });

  const debouncedValues = Object.fromEntries(
    Object.entries(localState).map(([key, value]) => [key, useDebounce(value, debounceMs)])
  );

  // --- 共通フィールドのuseEffect ---
  useEffect(() => {
    if (debouncedValues.nameInput !== searchConditions.name && debouncedValues.nameInput !== "") {
      updateSearchConditions({ name: debouncedValues.nameInput });
    } else if (debouncedValues.nameInput === "" && searchConditions.name !== null) {
      updateSearchConditions({ name: null });
    }
  }, [debouncedValues.nameInput, searchConditions.name, updateSearchConditions]);

  useEffect(() => {
    if (
      debouncedValues.phoneticNameInput !== searchConditions.phoneticName &&
      debouncedValues.phoneticNameInput !== ""
    ) {
      updateSearchConditions({ phoneticName: debouncedValues.phoneticNameInput });
    } else if (debouncedValues.phoneticNameInput === "" && searchConditions.phoneticName !== null) {
      updateSearchConditions({ phoneticName: null });
    }
  }, [debouncedValues.phoneticNameInput, searchConditions.phoneticName, updateSearchConditions]);

  useEffect(() => {
    const lat = debouncedValues.latInput === "" ? null : Number(debouncedValues.latInput);
    if (lat !== searchConditions.location?.lat) {
      updateSearchConditions({ location: { ...searchConditions.location, lat } });
    }
  }, [debouncedValues.latInput, searchConditions.location, updateSearchConditions]);

  useEffect(() => {
    const lon = debouncedValues.lonInput === "" ? null : Number(debouncedValues.lonInput);
    if (lon !== searchConditions.location?.lon) {
      updateSearchConditions({ location: { ...searchConditions.location, lon } });
    }
  }, [debouncedValues.lonInput, searchConditions.location, updateSearchConditions]);

  useEffect(() => {
    const newField = debouncedValues.sortFieldInput || null;
    if (newField !== searchConditions.sort?.field) {
      updateSearchConditions({ sort: { ...searchConditions.sort, field: newField } });
    }
  }, [debouncedValues.sortFieldInput, searchConditions.sort, updateSearchConditions]);

  useEffect(() => {
    const newOrder = debouncedValues.sortOrderInput || null;
    if (newOrder !== searchConditions.sort?.order) {
      updateSearchConditions({ sort: { ...searchConditions.sort, order: newOrder } });
    }
  }, [debouncedValues.sortOrderInput, searchConditions.sort, updateSearchConditions]);

  // --- DraftSearchにのみ存在するフィールドのuseEffect ---
  useEffect(() => {
    if ("publishedRevisionInput" in debouncedValues) {
      const newRevision = debouncedValues.publishedRevisionInput || null;
      if (newRevision !== (searchConditions as DraftSearchConditions).publishedRevisionId) {
        updateSearchConditions({ publishedRevisionId: newRevision });
      }
    }
  }, [debouncedValues.publishedRevisionInput, searchConditions, updateSearchConditions]);

  useEffect(() => {
    if ("hasUnpublishedChangesInput" in debouncedValues) {
      const newChanges = debouncedValues.hasUnpublishedChangesInput || null;
      if (newChanges !== (searchConditions as DraftSearchConditions).hasUnpublishedChanges) {
        updateSearchConditions({ hasUnpublishedChanges: newChanges });
      }
    }
  }, [debouncedValues.hasUnpublishedChangesInput, searchConditions, updateSearchConditions]);

  // searchConditionsがURL等で変更された場合にローカルstateを同期する
  useEffect(() => {
    const newLocalState: any = {
      nameInput: searchConditions.name || "",
      phoneticNameInput: searchConditions.phoneticName || "",
      latInput: searchConditions.location?.lat?.toString() || "",
      lonInput: searchConditions.location?.lon?.toString() || "",
      sortFieldInput: searchConditions.sort?.field || "",
      sortOrderInput: searchConditions.sort?.order || ""
    };
    if ("publishedRevisionId" in searchConditions) {
      newLocalState.publishedRevisionInput = searchConditions.publishedRevisionId || "";
    }
    if ("hasUnpublishedChanges" in searchConditions) {
      newLocalState.hasUnpublishedChangesInput = searchConditions.hasUnpublishedChanges || "";
    }
    setLocalState(newLocalState as L);
  }, [searchConditions]);

  const updateLocalState = (updates: Partial<L>) => {
    setLocalState((prev) => ({ ...prev, ...updates }));
  };

  const clearField = (field: keyof L) => {
    setLocalState((prev) => ({ ...prev, [field]: "" }) as L);
  };

  const setters = Object.keys(localState).reduce(
    (acc, key) => {
      const setterName = `set${key.charAt(0).toUpperCase() + key.slice(1)}`;
      acc[setterName] = (value: string) => updateLocalState({ [key]: value } as Partial<L>);
      return acc;
    },
    {} as Record<string, (value: string) => void>
  );

  return { localState, updateLocalState, clearField, setters };
};
